import { Icon } from '@blueprintjs/core'
import { DateRangeShortcut } from '@blueprintjs/datetime'
import { DateRange } from '@blueprintjs/datetime/src/common/dateRange'
import {
    addDays,
    format,
    parse,
    startOfMonth,
    startOfQuarter,
    startOfYear,
    subDays,
    subYears,
} from 'date-fns'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDatesFromUrl } from '../../hooks/useDatesFromUrl'
import { DateRangeSelect as PackageDateRangeSelect } from '../../packages/blueprint-components/src/DateRangeSelect'

const formatDate = (date: Date): string => {
    return format(date, 'M/d/y')
}
const parseDate = (date: string): Date => {
    return parse(date, 'M/d/y', new Date())
}

interface DateRangeSelectProps {
    value?: DateRange
    onChange: (selectedDates: DateRange) => void
    onShortcutChange?: (shortcut: DateRangeShortcut, index: number) => void
    selectedShortcutIndex?: number
    minDate?: Date
    maxDate?: Date
}

const DefaultMinDate = subYears(new Date(), 20)
export const DateRangeSelect = ({
    value = [subDays(new Date(), 30), new Date()],
    onChange,
    onShortcutChange = undefined,
    selectedShortcutIndex = undefined,
    minDate = DefaultMinDate,
    maxDate = new Date(),
}: DateRangeSelectProps) => {
    const { from, to } = useDatesFromUrl()
    const [localValue, setLocalValue] = useState<DateRange>([
        from ?? subDays(new Date(), 30),
        to ?? new Date(),
    ])

    useEffect(() => {
        setLocalValue([value[0] || subDays(new Date(), 30), value[1] || new Date()])
    }, [value])

    const fireOnChange = useCallback(
        ([from, to]) => {
            setLocalValue([from, to])

            if (from && to) {
                onChange([from, to])
            }
        },
        [onChange],
    )

    const shortcutsActual = useMemo(() => {
        return shortcuts.map((s) => {
            if (s.label === 'Lifetime') {
                s.dateRange = [minDate, today]
            }
            return s
        })
    }, [minDate])

    return (
        <PackageDateRangeSelect
            selectedShortcutIndex={selectedShortcutIndex}
            onShortcutChange={onShortcutChange}
            value={localValue}
            formatDate={formatDate}
            parseDate={parseDate}
            onChange={fireOnChange}
            className="mr-2"
            allowSingleDayRange
            contiguousCalendarMonths={false}
            shortcuts={shortcutsActual}
            minDate={minDate || DefaultMinDate}
            maxDate={maxDate}
            after={
                minDate != DefaultMinDate ? (
                    <div className="bg-mjGray-300 text-sm px-2 py-2.5 items-center rounded-b">
                        <Icon
                            icon={
                                <Icon icon="info-sign" size={20} className="mr-2 text-mjBlue-500" />
                            }
                        />
                        Amazon's API only allows data to be shown from 60 days before you signed up.
                    </div>
                ) : null
            }
        />
    )
}

const createShortcut = (label: string, dateRange: DateRange) => ({
    label,
    dateRange,
})

const today = new Date()

const singleDayShortcuts = [
    createShortcut('Today', [today, today]),
    createShortcut('Yesterday', [subDays(today, 1), subDays(today, 1)]),
]

export const shortcuts = [
    ...singleDayShortcuts,

    createShortcut('Last 7 days', [subDays(today, 7), today]),
    createShortcut('MTD', [startOfMonth(today), today]),
    createShortcut('Last 30 days', [subDays(today, 30), today]),
    createShortcut('QTD', [startOfQuarter(today), today]),
    createShortcut('Last 90 days', [subDays(today, 90), today]),
    // createShortcut("Past 6 months", [subDays(today, 180), today]),
    createShortcut('YTD', [startOfYear(today), today]),
    createShortcut('Last 365 days', [subDays(today, 365), today]),
    createShortcut('Lifetime', [addDays(DefaultMinDate, 1), today]),
]
